import React, {ReactElement, useMemo} from "react";
import {Body, Content, Footer, Title, ZeeroPopup} from "./custom/ZeeroPopup";
import ZeeroLoading from "./custom/ZeeroLoading";
import ZeeroButton from "./custom/ZeeroButton";
import {CDUploadStatus} from "../types/CDUpload";
import {DicomizeStatus} from "../types/Dicomize";
import {ErrorCode} from "../types/Error";
import {useTranslation} from "react-i18next";
import {faCircleCheck} from "@awesome.me/kit-92187fb46c/icons/classic/solid";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

export default function ProgressPopup({status, progress, error, onAbort, onCancel, closeAfterSuccess = true}: {
	status: CDUploadStatus | DicomizeStatus;
	progress: { handled: number; total: number };
	error: ErrorCode | null;
	onAbort: () => void;
	onCancel: () => void;
	closeAfterSuccess?: boolean
}): ReactElement {
	const {t} = useTranslation();

	const pct = (handled: number, total: number) => (total ? Math.floor((handled / total) * 100) : 0);

	const progressBarMessage = useMemo(() => {
		const {handled, total} = progress;
		const pctLabel = pct(handled, total);
		switch (status) {
			case "SENDING":
				return t("progress_sending", {pct: pctLabel})
			case "DICOMIZING":
				return t("progress_dicomizing", {pct: pctLabel})
			case "STORING":
				return t("progress_storing", {pct: pctLabel})
			default:
				return undefined;
		}
	}, [status, progress]);

	const statusText = useMemo(() => {
		switch (status) {
			case "SUCCESS":
				if (progress.handled == progress.total) {
					if (closeAfterSuccess) {
						return t("progress_operationSuccess", {handled: progress.handled, total: progress.total})
					} else {
						return t("progress_operationSuccessRecap", {
							handled: progress.handled,
							total: progress.total
						})
					}
				}
				return t("progress_operationFailedFilesMissing", {handled: progress.handled, total: progress.total})
			case "FAILED":
				if (error == ErrorCode.SERVICE_ABORT_FAILED) {
					return `${t("progress_operationAbortFailed")} ${t("error_contactSupport")}`;
				} else if (error == ErrorCode.SERVICE_PROGRESS_FAILED) {
					return `${t("progress_operationProgressFailed")} ${t("error_contactSupport")}`;
				}

				return t("progress_operationFailedFilesMissing", {handled: progress.handled, total: progress.total})
			case "ABORTING":
				return t("progress_aborting");
			case "ABORTED":
				return t("progress_aborted", {handled: progress.handled, total: progress.total});
			default:
				return t("progress_sessionInProgress");
		}
	}, [status, progress, error])

	const progressStyle = useMemo(() => {
		switch (status) {
			case "SUCCESS":
				if (progress.handled == progress.total) {
					return `success`;
				}
				return `error`;
			case "FAILED":
				return "error"
			case "ABORTED":
			case "ABORTING":
				return "warn";
			default:
				return "primary";
		}
	}, [status, progress]);

	const popupTitle = useMemo(() => {
		switch (status) {
			case "SUCCESS":
				if (progress.handled == progress.total) {
					return t("progress_popupTitleSuccess");
				}
				return t("progress_popupTitleFailed");
			case "FAILED":
				return t("progress_popupTitleFailed");
			case "ABORTED":
				return t("progress_popupTitleAborted");
			default:
				return t("progress_popupTitleInProgress");
		}
	}, [status]);

	return (
			<ZeeroPopup>
				<Content>
					<Title>{popupTitle}</Title>
					<Body>
						<div style={{marginBottom: "1.5em"}}>{statusText}</div>
						<div>
							<ZeeroLoading
									type={progressStyle}
									current={progress.handled}
									total={progress.total}
									message={progressBarMessage}
							/>
						</div>
						{(status == "SUCCESS" && !closeAfterSuccess) && (
								<div style={{textAlign: "center", paddingTop: "1em"}}>
									<FontAwesomeIcon icon={faCircleCheck} color="var(--success)"/> {t("progress_allDoneClose")}
								</div>
						)}
					</Body>
				</Content>
				<Footer>
					{status != "ABORTING" && (
							<React.Fragment>
								{status == "SUCCESS" || status == "FAILED" || status == "ABORTED" ? (
										<React.Fragment>
											{((status == "SUCCESS" && closeAfterSuccess && progress.handled == progress.total) ||
													(status == "SUCCESS" && progress.handled != progress.total) ||
													status == "FAILED" ||
													status == "ABORTED") && (
													<ZeeroButton size={"xs"} type="secondary" onClick={onCancel}>
														{t("general_close")}
													</ZeeroButton>
											)}
										</React.Fragment>
								) : (
										<ZeeroButton size={"sm"} type="warn" onClick={onAbort}>
											{t("general_abort")}
										</ZeeroButton>
								)}
							</React.Fragment>
					)}
				</Footer>
			</ZeeroPopup>
	);
}
